import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import { normalizeImagePathForStorage } from '../../utils/format';

export const fetchItems = createAsyncThunk(
  'inventory/fetchItems',
  async (categoryId, { rejectWithValue }) => {
    try {
      const res = await apiService.getProducts(categoryId);
      // Handle both { data: [...] } and direct [...] response formats
      const products = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      return products.map((p) => ({
        ...p,
        price: p.selling_price,
        image_url: p.image_path,
        category_name: p.category_name,
        category_color: p.category_color,
        unit_type: p.unit_type === 'kg' ? 'weight' : p.unit_type,
      }));
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'inventory/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiService.getCategories();
      // Handle both { data: [...] } and direct [...] response formats
      return Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const addItem = createAsyncThunk(
  'inventory/addItem',
  async (item, { rejectWithValue }) => {
    try {
      const payload = {
        name: item.name,
        sku: item.sku && String(item.sku).trim() ? String(item.sku).trim() : null,
        cost_price: parseFloat(item.cost_price) || 0,
        selling_price: parseFloat(item.selling_price) || 0,
        stock_quantity: parseFloat(item.stock_quantity) || 0,
        unit_type: item.unit_type === 'weight' ? 'kg' : 'piece',
        image_path: normalizeImagePathForStorage(item.image_url || item.image_path || null),
        category_id: item.category_id,
      };
      const res = await apiService.createProduct(payload);
      const p = res.data;
      return { ...p, price: p.selling_price, image_url: p.image_path, unit_type: p.unit_type === 'kg' ? 'weight' : p.unit_type };
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const updateItem = createAsyncThunk(
  'inventory/updateItem',
  async (item, { rejectWithValue }) => {
    try {
      const payload = {};
      if (item.name !== undefined) payload.name = item.name;
      if (item.sku !== undefined) payload.sku = item.sku;
      if (item.cost_price !== undefined) payload.cost_price = item.cost_price;
      if (item.selling_price !== undefined) payload.selling_price = item.selling_price;
      if (item.stock_quantity !== undefined) payload.stock_quantity = item.stock_quantity;
      if (item.unit_type !== undefined) payload.unit_type = item.unit_type === 'weight' ? 'kg' : item.unit_type;
      if (item.image_url !== undefined) {
        payload.image_path = normalizeImagePathForStorage(item.image_url);
      }
      if (item.category_id !== undefined) payload.category_id = item.category_id;
      const res = await apiService.updateProduct(item.id, payload);
      const p = res.data;
      return { ...p, price: p.selling_price, image_url: p.image_path, unit_type: p.unit_type === 'kg' ? 'weight' : p.unit_type };
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const deleteItem = createAsyncThunk(
  'inventory/deleteItem',
  async (itemId, { rejectWithValue }) => {
    try {
      await apiService.deleteProduct(itemId);
      return itemId;
    } catch (err) {
      return rejectWithValue(err.body || { error: err.message });
    }
  }
);

export const createCategory = createAsyncThunk(
  'inventory/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const res = await apiService.createCategory(categoryData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  'inventory/updateCategory',
  async ({ id, categoryData }, { rejectWithValue }) => {
    try {
      const res = await apiService.updateCategory(id, categoryData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'inventory/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteCategory(id);
      return { id };
    } catch (err) {
      // Preserve the full body so callers can read `errorCode` (e.g.
      // CATEGORY_HAS_PRODUCTS_IN_OFFERS) instead of a flattened string.
      return rejectWithValue(err.body || { error: err.message });
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    items: [],
    categories: [],
    loading: false,
    error: null,
    selectedCategory: null,
  },
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItems.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchItems.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(fetchItems.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchCategories.fulfilled, (state, action) => { state.categories = action.payload || []; })
      .addCase(addItem.fulfilled, (state, action) => { state.items.push(action.payload); })
      .addCase(updateItem.fulfilled, (state, action) => {
        const i = state.items.findIndex((x) => x.id === action.payload.id);
        if (i !== -1) state.items[i] = action.payload;
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.items = state.items.filter((x) => x.id !== action.payload);
      })
      .addCase(createCategory.fulfilled, (state, action) => { state.categories.push(action.payload); })
      .addCase(updateCategory.fulfilled, (state, action) => {
        const i = state.categories.findIndex((c) => c.id === action.payload.id);
        if (i !== -1) state.categories[i] = action.payload;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter((c) => c.id !== action.payload.id);
      });
  },
});

export const { setSelectedCategory, clearError } = inventorySlice.actions;
export default inventorySlice.reducer;
